// ModalCitizen is a helper for opening modals
// it ensures that only one instance of each modal type exists (that they are singletons)
// and that modals are attached to the DOM as direct children of <body>
//
// When using a Vue component, it must implement a openModal(params) method
// When using with an xhr modal, the backend should respond plain html, without layout, with a .sf-modal root element
//
// Usage (Vue):
//   1) modalCitizen.openModal('new-remark', {ledgerLabel: '03/2018', ledgerId: 55}) - Legacy way of opening a modal, disconnected from render tree / vuex store / vue context
//   2) modalCitizen.openModalPortal('account-mapping-edit-modal') - Component is organically part of the render tree, can be passed down props from parent, vuex store etc.
//   It is then teleported out of Vue context into a specific placeholder div, a direct ancestor of body
//
// Usage (Xhr):
//   modalCitizen.openXhrModal(event, 'edit-reconciliation-text', '/path/to/modal/action')
//   link_to_xhr_modal('edit-reconciliation-text', '/path/to/modal/action')
import type {
  ModalCitizenInterface,
  ModalIdentifier,
  ModalPayloadMap,
  ModalPayload,
  ModalPortalPayloadMap,
  OpenXhrModalOptions,
} from '@app/types'
import type {
  AvailableModals,
  ModalCommand,
  ModalPortalHandlerInterface,
  VueModalHandlerInterface,
  XhrModalHandlerInterface,
  DocumentSelectModalHandlerInterface,
  DocumentModalOptions,
} from './types'

import { maxReplayableCommands } from './constants'
import PortalModalHandler from './modal-handlers/portal-modal-handler'
import XhrModalHandler from './modal-handlers/xhr-modal-handler'
import VueModalHandler from './modal-handlers/vue-modal-handler'
import DocumentSelectModalHandler from './modal-handlers/document-select-modal-handler'
export default class ModalCitizen implements ModalCitizenInterface {
  #portalModalHandler: ModalPortalHandlerInterface
  #xhrModalHandler: XhrModalHandlerInterface
  #vueModalHandler: VueModalHandlerInterface
  #documentSelectModalHandler: DocumentSelectModalHandlerInterface
  #commandStack: ModalCommand[] = []

  constructor(availableModals: AvailableModals) {
    this.#portalModalHandler = new PortalModalHandler()
    this.#xhrModalHandler = new XhrModalHandler()
    this.#vueModalHandler = new VueModalHandler(availableModals)
    this.#documentSelectModalHandler = new DocumentSelectModalHandler()
  }

  openModal<T extends keyof ModalPayloadMap>(
    modalIdentifier: T,
    params: ModalPayload<T>,
    {
      autoClose = true,
    }: {
      autoClose?: boolean
    } = {}
  ): void {
    if (autoClose) {
      this.closeAllModals()
    }
    this.#vueModalHandler.open(modalIdentifier, params)
    this.#storeCommand(['vue', modalIdentifier, params])
  }

  openModalPortal<T extends keyof ModalPortalPayloadMap>(
    modalIdentifier: T,
    params?: ModalPayload<T>
  ): void {
    this.closeAllModals()
    this.#portalModalHandler.open(modalIdentifier, params)
    this.#storeCommand(['portal', modalIdentifier, params])
  }

  openXhrModal(
    event: Event,
    modalIdentifier: string,
    url: string,
    options: OpenXhrModalOptions = {}
  ): void {
    this.closeAllModals()
    void this.#xhrModalHandler.open(event, modalIdentifier, url, options)
    this.#storeCommand(['xhr', event, modalIdentifier, url, options])
  }

  closeModalXhr(modalIdentifier: string): void {
    this.#xhrModalHandler.close(modalIdentifier)
  }

  closeModalPortal<T extends keyof ModalPortalPayloadMap>(): T | null {
    return this.#portalModalHandler.close()
  }

  closeModal<T extends keyof ModalPayloadMap>(modalIdentifier: T): void {
    this.#vueModalHandler.close(modalIdentifier)
  }

  cleanup(): void {
    this.closeAllModals()
    this.#commandStack = []
  }

  closeAllModals(): void {
    this.#vueModalHandler.cleanup()
    this.cleanupXhrModals()
  }

  cleanupXhrModals(): void {
    this.#xhrModalHandler.cleanup()
  }

  hasOpenOverlayModal(): boolean {
    return (
      this.#xhrModalHandler.hasVisibleModals() ||
      this.#vueModalHandler.hasVisibleModals()
    )
  }

  hasOpenPortalModal(): boolean {
    return this.#portalModalHandler.hasOpenModal()
  }

  openDocumentSelectModal({
    firmId,
    companyId,
    options,
  }: DocumentModalOptions): void {
    this.#documentSelectModalHandler.open({
      firmId,
      companyId,
      options,
    })
  }

  openPreviousModalOf(id: ModalIdentifier): void {
    const reversedIndex = this.#commandStack
      .slice()
      .reverse()
      .findIndex((command) => {
        switch (command[0]) {
          case 'vue':
          case 'portal':
            return command[1] === id
          case 'xhr':
            return command[2] === id
          default:
            return false
        }
      })

    if (reversedIndex === -1) {
      return
    }

    const commandIndex = this.#commandStack.length - 1 - reversedIndex
    const command = this.#commandStack[commandIndex]
    this.#commandStack = this.#commandStack.slice(0, commandIndex + 1)

    switch (command[0]) {
      case 'vue':
        this.#vueModalHandler.open(command[1], command[2])
        break
      case 'portal':
        this.#portalModalHandler.open(command[1], command[2])
        break
      case 'xhr':
        void this.#xhrModalHandler.open(
          command[1],
          command[2],
          command[3],
          command[4]
        )
        break
      default:
        break
    }
  }

  documentSelectModalOpened(): void {
    this.#documentSelectModalHandler.modalOpened()
  }

  #storeCommand(command: ModalCommand): void {
    if (this.#commandStack.length >= maxReplayableCommands) {
      this.#commandStack.shift()
    }

    this.#commandStack.push(command)
  }
}
